import { StyledDialog } from "../../../components/StyledComponents/Dialog/Dialog.js";
import { Box, DialogContent } from "@mui/material";
import {
  BodyMedium,
  H6HeadlineSmall,
  H7TitleLarge,
  H9TitleSmall,
} from "../../../components/StyledComponents/Typography/Typography.tsx";
import { StyledIconButton } from "../../../components/StyledComponents/Buttons/AuradineButtons.js";
import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth0 } from "@auth0/auth0-react";
import useMinerPageContext from "../../context/useMinerPageContext.js";
import { format, parseISO } from "date-fns";
import { isEmpty } from "lodash";
import { is } from "date-fns/locale";
import { useTheme } from "../../../ThemeContext/ThemeContext.js";

const PSUHeader = ({ setOpen }) => {
  const { colors } = useTheme();
  return (
    <Box
      display={"flex"}
      flexDirection={"row"}
      justifyContent={"space-between"}
      flexGrow={1}
      padding={"1.5rem 0"}
    >
      <Box
        display={"flex"}
        flexDirection={"column"}
        gap={2}
      >
        <H6HeadlineSmall>{"PSU Stats"}</H6HeadlineSmall>

        <BodyMedium>
          Use this window to observe PSU stats.
        </BodyMedium>
      </Box>

      <StyledIconButton 
        colors={colors}
        onClick={() => {
          setOpen(false);
        }}
      >
        <CloseIcon />
      </StyledIconButton>
    </Box>
  );
};

const PSUForm = ({ setOpen, PSUData }) => {
  const { colors } = useTheme();
  return (
    <>
      <PSUHeader setOpen={setOpen} />
      <Box display={"flex"} flexGrow={1}>
        <Box
          display={"flex"}
          flexDirection={"column"}
          flexGrow={1}
          gap={2}
          padding={"0 1.5rem 1.5rem 1.5rem"}
        >
          {PSUData.map((PSU, index) => (
            <Box
              key={index}
              display={"grid"}
              gridTemplateColumns={"1fr"}
              gap={"1.5rem"}
              sx={{
                padding: "1.25rem 2rem 2rem 2rem",
                borderRadius: "1.5rem",
                backgroundColor: colors.background.accent,
              }}
            >
              {!isEmpty(PSU) &&
              Object.entries(PSU).length > 0 ? (
                Object.entries(PSU).map(
                  ([key, value], index) => (
                    <Box
                      display={"grid"}
                      gridTemplateColumns={"1fr 3fr"}
                      key={key + index}
                    >
                      <H9TitleSmall>{key} :</H9TitleSmall>
                      <BodyMedium>{value}</BodyMedium>
                    </Box>
                  )
                )
              ) : (
                <BodyMedium>No data available</BodyMedium>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </>
  );
};

const PSUDialog = ({ open, setOpen }) => {
  const { psuData } = useMinerPageContext();
  const psuDataToBeDisplayed =
    psuData && !Array.isArray(psuData) ? psuData : {};
  const { colors } = useTheme();

  // Support both old and new firmware keys for input voltage
  let inputVoltage1 = "";
  let inputVoltage2 = "";
  let inputVoltageLabel1 = "Input Voltage 1";
  let inputVoltageLabel2 = "Input Voltage 2";
  let psuStats = {};
  if (!isEmpty(psuDataToBeDisplayed)) {
    if (
      Array.isArray(psuDataToBeDisplayed.PSU) &&
      psuDataToBeDisplayed.PSU.length > 0
    ) {
      psuStats = psuDataToBeDisplayed.PSU[0];
    } else if (
      Array.isArray(psuDataToBeDisplayed.PSUStats) &&
      psuDataToBeDisplayed.PSUStats.length > 0
    ) {
      psuStats = psuDataToBeDisplayed.PSUStats[0];
    } else {
      psuStats = psuDataToBeDisplayed;
    }
    if (
      psuStats.Vin1 !== undefined ||
      psuStats.Vin2 !== undefined
    ) {
      inputVoltage1 = psuStats.Vin1 ?? "";
      inputVoltage2 = psuStats.Vin2 ?? "";
    } else if (psuStats.Vin !== undefined) {
      inputVoltage1 = psuStats.Vin;
      inputVoltageLabel1 = "Input Voltage";
      inputVoltage2 = "";
      inputVoltageLabel2 = "";
    }
  }
  const PSUMfrSerial = psuStats.MfrSerial ?? "";
  const PSUPower = psuStats.PowerIn ?? "";
  const PSUData = [
    {
      MfrSerial: PSUMfrSerial,
      Power: PSUPower,
      [inputVoltageLabel1]: inputVoltage1,
      ...(inputVoltageLabel2
        ? { [inputVoltageLabel2]: inputVoltage2 }
        : {}),
      Temp1: psuStats.Temp1 ?? "",
      Temp2: psuStats.Temp2 ?? "",
      Temp3: psuStats.Temp3 ?? "",
      FanSpeed1: psuStats.FanSpeed1 ?? "",
      PowerIn: psuStats.PowerIn ?? "",
      Manufacturer: psuStats.MfrId ?? "",
      PSUModel: psuStats.MfrModel ?? "",
      PSURevision: psuStats.MfrRevision ?? "",
      PriFwRev: psuStats.PriFwRev ?? "",
      // Add other fields as needed
    },
  ];
  return (
    <StyledDialog
      colors={colors}
      maxWidth={"md"}
      fullWidth
      open={open}
      hideBackdrop
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          setOpen(false);
        }
      }}
    >
      <DialogContent sx={{ padding: 0 }}>
        <PSUForm setOpen={setOpen} PSUData={PSUData} />
      </DialogContent>
    </StyledDialog>
  );
};

export default PSUDialog;
